import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { clienteService, Cliente, ClienteCreate, ClienteUpdate } from '../../services/clienteService';

interface ClienteFormProps {
  cliente?: Cliente | null;
  onClose: () => void;
  // Opcional: callback para cuando se crea/actualiza un cliente (para otros módulos como POS o Paquetes)
  onSuccess?: (cliente: Cliente) => void;
}

const ClienteForm = ({ cliente, onClose, onSuccess }: ClienteFormProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ClienteCreate | ClienteUpdate>({
    defaultValues: cliente ? {
      nombre: cliente.nombre,
      email: cliente.email,
      telefono: cliente.telefono,
      direccion: cliente.direccion,
      fechaNacimiento: cliente.fechaNacimiento ? cliente.fechaNacimiento.split('T')[0] : undefined
    } : {
      nombre: '',
      email: '',
      telefono: '',
      direccion: ''
    }
  });

  useEffect(() => {
    if (cliente) {
      reset({
        nombre: cliente.nombre,
        email: cliente.email,
        telefono: cliente.telefono,
        direccion: cliente.direccion,
        fechaNacimiento: cliente.fechaNacimiento ? cliente.fechaNacimiento.split('T')[0] : undefined
      });
    }
  }, [cliente, reset]);

  const createMutation = useMutation(clienteService.create, {
    onSuccess: (response) => {
      const created = response.data.cliente;
      toast.success('Cliente creado correctamente');
      queryClient.invalidateQueries(['clientes']);
      if (onSuccess && created) {
        onSuccess(created);
      }
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear cliente');
    }
  });

  const updateMutation = useMutation(
    (data: ClienteUpdate) => clienteService.update(cliente!.id, data),
    {
      onSuccess: (response) => {
        const updated = response.data.cliente;
        toast.success('Cliente actualizado correctamente');
        queryClient.invalidateQueries(['clientes']);
        if (onSuccess && updated) {
          onSuccess(updated);
        }
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar cliente');
      }
    }
  );

  const onSubmit = (data: ClienteCreate | ClienteUpdate) => {
    if (cliente) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data as ClienteCreate);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {cliente ? 'Editar Cliente' : 'Nuevo Cliente'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              type="text"
              {...register('nombre', { 
                required: 'Nombre es requerido',
                minLength: { value: 2, message: 'Mínimo 2 caracteres' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.nombre && (
              <p className="text-red-500 text-sm mt-1">{errors.nombre.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              {...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email inválido'
                }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <input
              type="text"
              {...register('telefono')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dirección
            </label>
            <textarea
              {...register('direccion')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Nacimiento
            </label>
            <input
              type="date"
              {...register('fechaNacimiento')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? 'Guardando...'
                : cliente
                ? 'Actualizar'
                : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClienteForm;

